import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

import type { DrfPortalTermDefinition, DrfTermContentState } from '../utils/drfTermsUtil';
import { SKIP_DRF } from '../constantsConfig/constantsConfig';
import { useAuth } from './useAuth';
import { useDrfTerms } from './useDrfTerms';
import { useCompanyVidaProtocolAtendimento, shouldSyncVidaAttendanceProtocol } from './vida/useVidaProtocolService';
import { apiService } from '../services/api.services';
import { flattenListaSubestipulantesResponse } from '../services/vida/subEstipulanteService';
import { getPersistedSelectedVinculoId, useCompanyStore } from '../store/companyStore';
import { useVidaProtocolAtendimentoStore } from '../store/vida/vidaProtocolServiceStore';
import { useUserStore } from '../store/userStore';
import type { Company, CompanyApoliceContext } from '../types';
import type { ListaSubestipulanteItem } from '../types/vida/subEstipulante/subEstipulanteType';
import { resolveIdentContratoFromPolicyRow } from '../types/companyType';
import type { EstipulanteUsuarioContext, VinculoEstipulanteItem } from '../types/estipulanteVinculoType';
import type { UserPoliciesListItem, UserPoliciesListResponse } from '../types/serviceType';
import { formatDocumentNumberInput, cleanDocumentAlnum } from '../utils/documentNumberUtils';
import { resolveUserDisplayName } from '../utils/entraIdUserMapper';
import {
  backofficeSearchProductToRamo,
  resolveEstipulanteDocumentoFields,
  type BackofficeSearchProduct,
} from '../utils/estipulanteLegacyKey';
import {
  applyPlaceholderApoliceBackofficeEntry,
  isPlaceholderApoliceVinculo,
  shouldUsePlaceholderApoliceBackofficeFlow,
} from '../pages/company/companyPlaceholderApoliceFlow';

export function pickPolicyRow(
  listRes: UserPoliciesListResponse,
  numApolice: number,
): UserPoliciesListItem | undefined {
  const rows = listRes
    .flatMap((g) => g.retorno ?? [])
    .filter((x): x is UserPoliciesListItem => x != null);
  if (rows.length === 0) return undefined;
  const match = rows.find((r) => {
    if (r.numApoliceApol == null) return false;
    const n = Number(String(r.numApoliceApol).replace(/\D/g, ''));
    return Number.isFinite(n) && n === numApolice;
  });
  return match ?? rows[0];
}

export function toCompanyApoliceContext(row: UserPoliciesListItem): CompanyApoliceContext {
  return {
    identContratoApol: row.identContratoApol,
    identContrato: row.identContrato,
    identEstipulanteApol: row.identEstipulanteApol,
    numApoliceApol: row.numApoliceApol,
    codEmpresaApol: row.codEmpresaApol,
    codSucursalApol: row.codSucursalApol,
    codRamoApol: row.codRamoApol,
    codprodutoApol: row.codprodutoApol,
    numDocumentoApol: row.numDocumentoApol,
  };
}

/** Only the auto-selection of persisted vinculo (reload) passes `attendanceProtocolFromLocalStorageOnly`; clicking “Select…” does not pass → allows GET Vida. */
export type HandleVinculoConfirmOptions = {
  attendanceProtocolFromLocalStorageOnly?: boolean;
};

export interface UseCompanyReturn {
  vinculosList: VinculoEstipulanteItem[];
  sessionRole: string | null;
  selectedCompany: Company | null;
  showCloseToHome: boolean;
  pendingItem: VinculoEstipulanteItem | null;
  setPendingItem: Dispatch<SetStateAction<VinculoEstipulanteItem | null>>;
  vinculoConfirmLoading: boolean;
  initialVinculosLoading: boolean;
  vinculosLoadError: boolean;
  autoSelectingPersistedVinculo: boolean;
  vinculoConfirmError: string | null;
  vinculoSearchTerm: string;
  setVinculoSearchTerm: Dispatch<SetStateAction<string>>;
  vinculoSortBy: string;
  setVinculoSortBy: Dispatch<SetStateAction<string>>;
  showVinculoSorting: boolean;
  setShowVinculoSorting: Dispatch<SetStateAction<boolean>>;
  backofficeSearchTerm: string;
  setBackofficeSearchTerm: Dispatch<SetStateAction<string>>;
  backofficeSearchProduct: BackofficeSearchProduct | null;
  backofficeSearchRamo: { ramoCodigo: string; ramoNome: string } | null;
  handleBackofficeProductSelect: (product: BackofficeSearchProduct) => void;
  backofficeResult: UserPoliciesListItem | null;
  setBackofficeResult: Dispatch<SetStateAction<UserPoliciesListItem | null>>;
  backofficeSelected: boolean;
  setBackofficeSelected: Dispatch<SetStateAction<boolean>>;
  isBackofficeSession: boolean;
  shouldBlockCompanyPageWithWhiteLoading: boolean;
  filteredAndSortedVinculos: VinculoEstipulanteItem[];
  handleVinculoSelect: (item: VinculoEstipulanteItem) => void;
  handleVinculoConfirm: (
    item: VinculoEstipulanteItem,
    options?: HandleVinculoConfirmOptions,
  ) => Promise<boolean>;
  handleBackofficeSearch: () => Promise<void>;
  handleBackofficeConfirm: () => Promise<void>;
  retryLoadVinculos: () => Promise<void>;
  clearCompanySession: () => void;
  termsCheckLoading: boolean;
  showDrfTermModal: boolean;
  showDrfListTermsFailure: boolean;
  drfTermsSaveLoading: boolean;
  drfTermsSaveError: string | null;
  handleDrfTermsSave: () => Promise<void>;
  retryDrfTermsSave: () => void;
  retryDrfTermsGate: () => void;
  handleDrfTermsExit: () => void;
  termContentByTab: Record<string, DrfTermContentState>;
  allDrfTabsViewed: boolean;
  drfPortalTermDefinitions: readonly DrfPortalTermDefinition[];
  selectDrfTermTab: (tabId: string) => void;
  userDisplayName: string;
  skipDrf: boolean;
}

export function useCompany(
  vinculoSortPanelRef: RefObject<HTMLDivElement | null>,
): UseCompanyReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const getPoliciesByApoliceNumber = useCallback(
    async (apoliceNumber: number): Promise<UserPoliciesListResponse> => {
      return apiService.policyholder.getUserPoliciesList({ numApoliceApol: apoliceNumber });
    },
    [],
  );

  const getPolicyRowsByApoliceNumber = useCallback(
    async (apoliceNumber: number): Promise<UserPoliciesListItem[]> => {
      const response = await apiService.policyholder.getUserPoliciesList({ numApoliceApol: apoliceNumber });
      return response
        .flatMap((group) => group.retorno ?? [])
        .filter((item): item is UserPoliciesListItem => item != null);
    },
    [],
  );

  const { syncProtocolAfterPolicyRow } = useCompanyVidaProtocolAtendimento();
  const { logout } = useAuth();
  const user = useUserStore((state) => state.user);
  const {
    termsCheckLoading,
    showDrfTermModal,
    showDrfListTermsFailure,
    saveLoading: drfTermsSaveLoading,
    saveError: drfTermsSaveError,
    runTermsGate,
    termContentByTab,
    allTabsViewed: allDrfTabsViewed,
    portalTermDefinitions: drfPortalTermDefinitions,
    selectTermTab: selectDrfTermTab,
    savePendingTerms,
    retryLastAction,
  } = useDrfTerms(user?.cpf);
  const termsGateStartedRef = useRef(false);
  const {
    vinculos: vinculosList,
    sessionRole,
    selectedCompany,
    setSelectedCompany,
    setSelectedVinculo,
    setSessionRole,
    setVinculos,
    loadVinculosByPessoaId,
    clearCompanySession,
  } = useCompanyStore();

  const [pendingItem, setPendingItem] = useState<VinculoEstipulanteItem | null>(null);
  const [vinculoConfirmLoading, setVinculoConfirmLoading] = useState(false);
  const [initialVinculosLoading, setInitialVinculosLoading] = useState(true);
  const [vinculosLoadError, setVinculosLoadError] = useState(false);
  const [autoSelectingPersistedVinculo, setAutoSelectingPersistedVinculo] = useState(false);
  const [vinculoConfirmError, setVinculoConfirmError] = useState<string | null>(null);
  const [vinculoSearchTerm, setVinculoSearchTerm] = useState('');
  const [vinculoSortBy, setVinculoSortBy] = useState('policy-asc');
  const [showVinculoSorting, setShowVinculoSorting] = useState(false);
  const [backofficeSearchTerm, setBackofficeSearchTerm] = useState('');
  const [backofficeSearchProduct, setBackofficeSearchProduct] = useState<BackofficeSearchProduct | null>(null);
  const [backofficeResult, setBackofficeResult] = useState<UserPoliciesListItem | null>(null);
  const [backofficeSelected, setBackofficeSelected] = useState(false);

  const backofficeSearchRamo = useMemo(
    () => (backofficeSearchProduct ? backofficeSearchProductToRamo(backofficeSearchProduct) : null),
    [backofficeSearchProduct],
  );

  const handleBackofficeProductSelect = useCallback((product: BackofficeSearchProduct) => {
    setBackofficeSearchProduct(product);
    setBackofficeResult(null);
    setBackofficeSelected(false);
    setVinculoConfirmError(null);
  }, []);
  const autoConfirmedVinculoIdRef = useRef<string | null>(null);

  const isBackofficeSession = String(sessionRole ?? '').trim().toUpperCase() === 'BACKOFFICE';
  const fromLogin = Boolean((location.state as { fromLogin?: boolean } | null)?.fromLogin);
  // Close: confirmed policy, or backoffice who opened /company from navbar (not post-login).
  const showCloseToHome =
    Boolean(selectedCompany?.policySelection?.numeroApolice) ||
    (isBackofficeSession && !fromLogin);
  const redirectedByProtectedRoute = Boolean(
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname,
  );
  const shouldAutoSelectPersistedVinculo =
    !fromLogin && (navigationType === 'POP' || redirectedByProtectedRoute);
  const persistedVinculoId = getPersistedSelectedVinculoId();
  const hasPersistedVinculoToAutoSelect = Boolean(
    shouldAutoSelectPersistedVinculo &&
    persistedVinculoId &&
    autoConfirmedVinculoIdRef.current !== persistedVinculoId,
  );
  const hasPersistedVinculoInCurrentList = Boolean(
    persistedVinculoId &&
    vinculosList.some((item) => item.vinculo.idVinculoNegocio === persistedVinculoId),
  );
  const shouldBlockCompanyPageWithWhiteLoading = Boolean(
    hasPersistedVinculoToAutoSelect &&
    (vinculoConfirmLoading || vinculosList.length === 0 || hasPersistedVinculoInCurrentList),
  );

  useEffect(() => {
    if (!shouldBlockCompanyPageWithWhiteLoading) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      clearCompanySession();
      navigate('/login', { replace: true });
    }, 9000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [clearCompanySession, navigate, shouldBlockCompanyPageWithWhiteLoading]);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [navigate, user]);

  useEffect(() => {
    if (showVinculoSorting) {
      const firstControl = vinculoSortPanelRef.current?.querySelector<HTMLElement>(
        'select, button, [tabindex]:not([tabindex="-1"])',
      );
      firstControl?.focus();
    }
  }, [showVinculoSorting, vinculoSortPanelRef]);

  const loadBusinessLinks = useCallback(async (): Promise<{ ok: boolean; hasVinculos: boolean }> => {
    if (isBackofficeSession || user?.idPessoa == null) {
      return { ok: true, hasVinculos: false };
    }

    setVinculoConfirmLoading(true);
    setVinculosLoadError(false);
    try {
      const ok = await loadVinculosByPessoaId(String(user.idPessoa));
      const hasVinculos = useCompanyStore.getState().vinculos.length > 0;
      if (!ok) {
        setVinculosLoadError(true);
      }
      return { ok, hasVinculos };
    } finally {
      setVinculoConfirmLoading(false);
    }
  }, [isBackofficeSession, loadVinculosByPessoaId, user?.idPessoa]);

  useEffect(() => {
    if (isBackofficeSession) {
      setInitialVinculosLoading(false);
      return;
    }
    if (user?.idPessoa == null) {
      return;
    }
    if (termsGateStartedRef.current) {
      return;
    }
    termsGateStartedRef.current = true;

    void (async () => {
      try {
        const { hasVinculos } = await loadBusinessLinks();
        if (hasVinculos && !SKIP_DRF) {
          await runTermsGate();
        }
      } finally {
        setInitialVinculosLoading(false);
      }
    })();
  }, [isBackofficeSession, loadBusinessLinks, runTermsGate, user?.idPessoa]);

  const handleDrfTermsSave = useCallback(async () => {
    const saved = await savePendingTerms();
    if (!saved) {
      return;
    }
    setInitialVinculosLoading(true);
    try {
      await loadBusinessLinks();
    } finally {
      setInitialVinculosLoading(false);
    }
  }, [loadBusinessLinks, savePendingTerms]);

  const retryDrfTermsSave = useCallback(() => {
    void retryLastAction();
  }, [retryLastAction]);

  const retryDrfTermsGate = useCallback(() => {
    void runTermsGate();
  }, [runTermsGate]);

  const handleDrfTermsExit = useCallback(() => {
    void logout();
  }, [logout]);

  const retryLoadVinculos = useCallback(async () => {
    setInitialVinculosLoading(true);
    try {
      await loadBusinessLinks();
    } finally {
      setInitialVinculosLoading(false);
    }
  }, [loadBusinessLinks]);

  /** Reload: vínculo Vida (4) — lê `company.vida.*` via `vidaProtocolStorage`, regrava com `persistVidaProtocolCredentials` e hidrata a store (sem GET). */
  useEffect(() => {
    if (isBackofficeSession || vinculosList.length === 0 || !persistedVinculoId) {
      return;
    }
    const persistedItem = vinculosList.find(
      (row) => row.vinculo.idVinculoNegocio === persistedVinculoId,
    );
    if (!persistedItem) {
      return;
    }
    useVidaProtocolAtendimentoStore.getState().hydrateFromPersistedStorageIfRamo4Vinculo(persistedItem);
  }, [isBackofficeSession, persistedVinculoId, vinculosList]);

  const filteredAndSortedVinculos = useMemo(() => {
    let list = vinculosList;
    const term = vinculoSearchTerm.trim().toLowerCase();
    if (term !== '') {
      const termDigits = term.replace(/\D/g, '');
      list = list.filter((item) => {
        const p = item.parsed;
        const papelDesc = item.vinculo.representacao?.papel?.descricao ?? '';
        const papelId = item.vinculo.representacao?.papel?.id ?? '';
        const documentoAlnum = cleanDocumentAlnum(p.documento ?? p.cnpj);
        const documentoFmt = formatDocumentNumberInput(p.documento ?? p.cnpj).toLowerCase();
        return (
          String(p.numeroApolice).toLowerCase().includes(term) ||
          (termDigits.length > 0 && documentoAlnum.includes(termDigits)) ||
          documentoFmt.includes(term) ||
          documentoAlnum.toLowerCase().includes(term) ||
          p.ramoNome.toLowerCase().includes(term) ||
          String(p.ramoCodigo).includes(term) ||
          papelDesc.toLowerCase().includes(term) ||
          String(papelId).toLowerCase().includes(term)
        );
      });
    }
    const sorted = [...list].sort((a, b) => {
      const pa = a.parsed;
      const pb = b.parsed;
      const papelA = a.vinculo.representacao?.papel?.descricao ?? '';
      const papelB = b.vinculo.representacao?.papel?.descricao ?? '';
      switch (vinculoSortBy) {
        case 'policy-asc':
          return String(pa.numeroApolice).localeCompare(String(pb.numeroApolice), undefined, { numeric: true });
        case 'policy-desc':
          return String(pb.numeroApolice).localeCompare(String(pa.numeroApolice), undefined, { numeric: true });
        case 'cnpj-asc':
          return (pa.documento ?? pa.cnpj).localeCompare(pb.documento ?? pb.cnpj);
        case 'cnpj-desc':
          return (pb.documento ?? pb.cnpj).localeCompare(pa.documento ?? pa.cnpj);
        case 'ramo-asc':
          return pa.ramoNome.localeCompare(pb.ramoNome);
        case 'ramo-desc':
          return pb.ramoNome.localeCompare(pa.ramoNome);
        case 'papel-asc':
          return papelA.localeCompare(papelB);
        case 'papel-desc':
          return papelB.localeCompare(papelA);
        default:
          return 0;
      }
    });
    return sorted;
  }, [vinculosList, vinculoSearchTerm, vinculoSortBy]);

  const handleVinculoSelect = useCallback((item: VinculoEstipulanteItem) => {
    setPendingItem(item);
  }, []);

  const handleVinculoConfirm = useCallback(
    async (item: VinculoEstipulanteItem, options?: HandleVinculoConfirmOptions): Promise<boolean> => {
      const v = item.vinculo;
      const papel = v.representacao?.papel;
      const numeroApoliceDigits = String(item.parsed.numeroApolice).replace(/\D/g, '');
      const numApoliceNum = Number(numeroApoliceDigits);
      const docFields = resolveEstipulanteDocumentoFields(item.parsed.documento ?? item.parsed.cnpj);
      const baseContext: EstipulanteUsuarioContext = {
        ...docFields,
        tipoDocumento: item.parsed.tipoDocumento ?? docFields.tipoDocumento,
        numeroApolice: item.parsed.numeroApolice,
        ramoCodigo: item.parsed.ramoCodigo,
        ramoNome: item.parsed.ramoNome,
        papelId: papel?.id ?? '',
        papelDescricao: papel?.descricao ?? '',
        chaveLegado: v.representacao?.chaveLegado ?? '',
      };

      if (
        !isPlaceholderApoliceVinculo(item) &&
        (!Number.isFinite(numApoliceNum) || numApoliceNum <= 0)
      ) {
        setVinculoConfirmError('Número da apólice inválido para este vínculo.');
        return false;
      }

      setVinculoConfirmError(null);
      setVinculoConfirmLoading(true);
      setSelectedVinculo(v);

      const clearSelectedVinculoOnFailure = () => {
        setSelectedVinculo(null);
      };

      try {
        const rawListRes = await getPoliciesByApoliceNumber(numApoliceNum);
        const listRes = Array.isArray(rawListRes) ? rawListRes : [];

        if (shouldUsePlaceholderApoliceBackofficeFlow(item, listRes)) {
          applyPlaceholderApoliceBackofficeEntry({
            item,
            setSessionRole,
            setSelectedCompany,
            setSelectedVinculo,
          });
          setVinculos(vinculosList);
          navigate('/home', { replace: true });
          return true;
        }

        const rows = listRes.flatMap((g) => g.retorno ?? []).filter(Boolean);
        if (rows.length === 0) {
          setVinculoConfirmError(
            'Não foi possível listar as apólices para este vínculo. Tente novamente.',
          );
          clearSelectedVinculoOnFailure();
          return false;
        }

        const policySelection: EstipulanteUsuarioContext = {
          ...baseContext,
          policiesListResponse: listRes,
        };
        const policyRow = pickPolicyRow(listRes, numApoliceNum);
        const apoliceContext = policyRow ? toCompanyApoliceContext(policyRow) : undefined;

        const identContrato = policyRow ? resolveIdentContratoFromPolicyRow(policyRow) : undefined;
        if (!identContrato) {
          setVinculoConfirmError(
            'Não foi possível identificar o contrato desta apólice para carregar os subestipulantes.',
          );
          clearSelectedVinculoOnFailure();
          return false;
        }

        const estipulanteRamo = {
          codigo: item.parsed.ramoCodigo,
          nome: item.parsed.ramoNome,
        };
        const mustSyncVidaProtocol = shouldSyncVidaAttendanceProtocol(policyRow, estipulanteRamo);

        await syncProtocolAfterPolicyRow(
          policyRow,
          user?.idPessoa,
          estipulanteRamo,
          mustSyncVidaProtocol && options?.attendanceProtocolFromLocalStorageOnly
            ? { skipRemoteProtocolRequest: true, vinculoItem: item }
            : undefined,
        );

        let subestipulantes: ListaSubestipulanteItem[] = [];
        try {
          const rawSub = await apiService.subEstipulante.getListaSubestipulantes(identContrato);
          subestipulantes = flattenListaSubestipulantesResponse(rawSub);
        } catch (subErr) {
          const msg =
            subErr instanceof Error
              ? subErr.message
              : 'Não foi possível carregar a lista de subestipulantes. Tente novamente.';
          setVinculoConfirmError(msg);
          clearSelectedVinculoOnFailure();
          return false;
        }

        setVinculos(vinculosList);
        setSelectedCompany({
          id: v.idVinculoNegocio,
          apoliceContext,
          policySelection,
          subestipulantes,
        });
        navigate('/home', { replace: true });
        return true;
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Erro ao confirmar vínculo. Tente novamente.';
        setVinculoConfirmError(msg);
        clearSelectedVinculoOnFailure();
        return false;
      } finally {
        setVinculoConfirmLoading(false);
      }
    },
    [
      getPoliciesByApoliceNumber,
      navigate,
      setSelectedCompany,
      setSelectedVinculo,
      setSessionRole,
      setVinculos,
      syncProtocolAfterPolicyRow,
      user?.idPessoa,
      vinculosList,
    ],
  );

  useEffect(() => {
    if (isBackofficeSession || !shouldAutoSelectPersistedVinculo || vinculosList.length === 0) {
      return;
    }
    if (!persistedVinculoId) {
      return;
    }
    if (autoConfirmedVinculoIdRef.current === persistedVinculoId) {
      return;
    }
    const persistedItem = vinculosList.find(
      (item) => item.vinculo.idVinculoNegocio === persistedVinculoId,
    );
    if (!persistedItem) {
      return;
    }
    setPendingItem(persistedItem);
    void (async () => {
      setAutoSelectingPersistedVinculo(true);
      try {
        const confirmed = await handleVinculoConfirm(persistedItem, {
          attendanceProtocolFromLocalStorageOnly: true,
        });
        if (confirmed) {
          autoConfirmedVinculoIdRef.current = persistedVinculoId;
        }
      } finally {
        setAutoSelectingPersistedVinculo(false);
      }
    })();
  }, [handleVinculoConfirm, isBackofficeSession, shouldAutoSelectPersistedVinculo, vinculosList]);

  const handleBackofficeSearch = useCallback(async () => {
    if (!backofficeSearchProduct) {
      setVinculoConfirmError('Selecione o produto (Vida ou Previdência) antes de buscar.');
      return;
    }

    const apoliceDigits = backofficeSearchTerm.replace(/\D/g, '');
    const apoliceNumber = Number(apoliceDigits);
    if (!Number.isFinite(apoliceNumber) || apoliceNumber <= 0) {
      setBackofficeResult(null);
      setBackofficeSelected(false);
      setVinculoConfirmError('Informe um número de apólice válido para buscar.');
      return;
    }

    setVinculoConfirmError(null);
    setBackofficeResult(null);
    setBackofficeSelected(false);
    setVinculoConfirmLoading(true);
    try {
      const rows = await getPolicyRowsByApoliceNumber(apoliceNumber);
      if (rows.length === 0) {
        setVinculoConfirmError('Nenhuma apólice encontrada para o número informado.');
        return;
      }
      setBackofficeResult(rows[0]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Erro ao buscar apólice. Tente novamente.';
      setVinculoConfirmError(msg);
    } finally {
      setVinculoConfirmLoading(false);
    }
  }, [backofficeSearchProduct, backofficeSearchTerm, getPolicyRowsByApoliceNumber]);

  const handleBackofficeConfirm = useCallback(async () => {
    if (!backofficeResult || !backofficeSearchRamo) return;
    const numeroApolice = String(backofficeResult.numApoliceApol ?? '').trim();
    if (!numeroApolice) {
      setVinculoConfirmError('Apólice inválida para seleção.');
      return;
    }

    setVinculoConfirmError(null);
    setVinculoConfirmLoading(true);
    let subestipulantes: ListaSubestipulanteItem[] = [];
    try {
      const identContrato = resolveIdentContratoFromPolicyRow(backofficeResult);
      if (!identContrato) {
        setVinculoConfirmError(
          'Não foi possível identificar o contrato desta apólice para carregar os subestipulantes.',
        );
        return;
      }
      const estipulanteRamo = {
        codigo: backofficeSearchRamo.ramoCodigo,
        nome: backofficeSearchRamo.ramoNome,
      };
      await syncProtocolAfterPolicyRow(backofficeResult, user?.idPessoa, estipulanteRamo);
      const rawSub = await apiService.subEstipulante.getListaSubestipulantes(identContrato);
      subestipulantes = flattenListaSubestipulantesResponse(rawSub);
    } catch (e) {
      const msg =
        e instanceof Error
          ? e.message
          : 'Erro ao carregar protocolo ou subestipulantes. Tente novamente.';
      setVinculoConfirmError(msg);
      return;
    } finally {
      setVinculoConfirmLoading(false);
    }

    const docFields = resolveEstipulanteDocumentoFields(String(backofficeResult.numDocumentoApol ?? ''));
    const policySelection: EstipulanteUsuarioContext = {
      ...docFields,
      numeroApolice,
      ramoCodigo: backofficeSearchRamo.ramoCodigo,
      ramoNome: backofficeSearchRamo.ramoNome,
      papelId: 'BACKOFFICE',
      papelDescricao: 'BACKOFFICE',
      chaveLegado: '',
      policiesListResponse: [{ retorno: [backofficeResult], mensagem: 'Consulta efetuada com sucesso!' }],
    };

    setSelectedVinculo(null);
    setVinculos([]);
    setSelectedCompany({
      id: String(backofficeResult.identContratoApol ?? backofficeResult.identContrato ?? numeroApolice),
      apoliceContext: toCompanyApoliceContext(backofficeResult),
      policySelection,
      subestipulantes,
    });
    navigate('/home', { replace: true });
  }, [
    backofficeResult,
    backofficeSearchRamo,
    navigate,
    setSelectedCompany,
    setSelectedVinculo,
    setVinculos,
    syncProtocolAfterPolicyRow,
    user?.idPessoa,
  ]);

  return {
    vinculosList,
    sessionRole,
    selectedCompany,
    showCloseToHome,
    pendingItem,
    setPendingItem,
    vinculoConfirmLoading,
    initialVinculosLoading,
    vinculosLoadError,
    autoSelectingPersistedVinculo,
    vinculoConfirmError,
    vinculoSearchTerm,
    setVinculoSearchTerm,
    vinculoSortBy,
    setVinculoSortBy,
    showVinculoSorting,
    setShowVinculoSorting,
    backofficeSearchTerm,
    setBackofficeSearchTerm,
    backofficeSearchProduct,
    backofficeSearchRamo,
    handleBackofficeProductSelect,
    backofficeResult,
    setBackofficeResult,
    backofficeSelected,
    setBackofficeSelected,
    isBackofficeSession,
    shouldBlockCompanyPageWithWhiteLoading,
    filteredAndSortedVinculos,
    handleVinculoSelect,
    handleVinculoConfirm,
    handleBackofficeSearch,
    handleBackofficeConfirm,
    retryLoadVinculos,
    clearCompanySession,
    termsCheckLoading,
    showDrfTermModal,
    showDrfListTermsFailure,
    drfTermsSaveLoading,
    drfTermsSaveError,
    handleDrfTermsSave,
    retryDrfTermsSave,
    retryDrfTermsGate,
    handleDrfTermsExit,
    termContentByTab,
    allDrfTabsViewed,
    drfPortalTermDefinitions,
    selectDrfTermTab,
    userDisplayName: resolveUserDisplayName(user, 'Usuário'),
    skipDrf: SKIP_DRF,
  };
}
